# Dev Mode

Enable `devMode` during development to catch common mistakes early. Disable it in production to skip validation checks.

```ts
export const taias = createTaias({
  flow: musicDiscoveryFlow,
  devMode: process.env.NODE_ENV === "development",
});
```

---

## Validation Checks

### Duplicate match conditions

If you define two steps with the same match condition in a flow:

```ts
flow.step(
  { toolName: { is: "get_recent_tracks" } },
  { nextTool: "get_recommendations" },
);
flow.step(
  { toolName: { is: "get_recent_tracks" } },
  { nextTool: "create_playlist" },
); // Error
```

Taias will throw on initialization:

```
Taias: Duplicate match condition in flow 'music_discovery_flow'. Each step must have a unique match condition.
```

Each step in a flow must have a unique match condition. Duplicate detection compares the full serialized match condition object.

Duplicate detection also applies to `isNot` conditions and compound conditions:

```ts
flow.step({ toolName: { isNot: "abort" } }, { nextTool: "first" });
flow.step({ toolName: { isNot: "abort" } }, { nextTool: "second" }); // duplicate

flow.step(
  { toolName: { is: "scan_repo" }, params: { language: { is: "python" } } },
  { nextTool: "first" },
);
flow.step(
  { toolName: { is: "scan_repo" }, params: { language: { is: "python" } } },
  { nextTool: "second" },
); // duplicate
```

Note that `{ is: "X" }` and `{ isNot: "X" }` are **not** duplicates -- they are different operators with different semantics:

```ts
flow.step({ toolName: { is: "scan_repo" } }, { nextTool: "configure_app" });
flow.step({ toolName: { isNot: "scan_repo" } }, { nextTool: "continue_flow" }); // OK - different operators
```

Similarly, steps with different `params` or `result` conditions are not duplicates even if they share the same `toolName`:

```ts
flow.step(
  { toolName: { is: "scan_repo" }, params: { language: { is: "python" } } },
  { nextTool: "configure_python" },
);
flow.step(
  { toolName: { is: "scan_repo" }, params: { language: { is: "go" } } },
  { nextTool: "configure_go" },
); // OK - different params conditions
```

---

### Empty nextTool

If a step returns an empty string for nextTool:

```ts
flow.step({ toolName: { is: "get_recent_tracks" } }, { nextTool: "" }); // Warning
```

Taias logs a warning:

```
Taias: nextTool for tool 'get_recent_tracks' is empty.
```

This catches typos and logic errors where you meant to specify a next tool.

---

## UI Affordance Validations

When using UI affordances, dev mode adds additional validation checks.

### Duplicate handleId

If two handles share the same `handleId`:

```ts
const widgetA = defineAffordances((r) => {
  r.primaryCta("tracks.cta.recommend", { toolName: "get_recommendations" });
});

const widgetB = defineAffordances((r) => {
  r.primaryCta("tracks.cta.recommend", { toolName: "create_playlist" }); // Error: same handleId
});

mergeAffordances([widgetA, widgetB], { devMode: true });
```

Taias will throw:

```
Error: [Taias] Duplicate handleId "tracks.cta.recommend"
```

### Ambiguous slot binding

If two handles bind to the same `{slot, key, value}` triple:

```ts
const affordances = defineAffordances((r) => {
  r.primaryCta("option-a", { toolName: "get_recommendations" });
  r.primaryCta("option-b", { toolName: "get_recommendations" }); // Error: ambiguous
});
```

Taias will throw:

```
Error: [Taias] Ambiguous affordance: slot "primaryCta" has multiple handles bound to (nextTool="get_recommendations")
```

This prevents selection ambiguity - for any `{slot, toolName}`, there can only be one matching handle.

### Missing affordance for slot

If your flow decision points to a tool that has no registered handle for a slot:

```ts
// Flow returns nextTool: "share_playlist"
// But no handle is registered with { toolName: "share_playlist" }
```

Taias logs a warning at resolve time:

```
Warning: [Taias] No affordance for slot "primaryCta" when nextTool="share_playlist"
```

This helps catch missing registrations before they cause UI issues.

---

## Production Behavior

When `devMode: false` (or omitted), validation checks are skipped for performance.
